/**
 * Pinch-to-zoom/pan arithmetic for the chapter illustration's full-screen
 * view (src/components/ChapterSceneFullscreen.tsx) — pure so the clamping
 * and reset rules are testable without a DOM or real pointer events, same
 * split as chapterScenes.ts.
 *
 * Deliberately a light implementation (product ask): scale only clamps to
 * a fixed range, double-tap only toggles between 1x and a fixed 2x, and
 * panning is unclamped (a child dragging a zoomed image off-centre is a
 * lot less of a problem than the added complexity of computing per-scale
 * pan bounds would be worth here).
 */

/** Never smaller than the image's natural size... */
export const MIN_SCALE = 1;
/** ...nor more than 3x it. Lowered from 4x (iPhone bug report, 2026-09-27):
 *  a 1024x1536 image scaled up this far, backed by a GPU compositing layer
 *  sized for the full viewport at device pixel ratio, is exactly the kind
 *  of allocation that can push a memory-constrained WebContent process
 *  over the edge — see ChapterSceneFullscreen.tsx's rendering comments for
 *  the rest of that defensive change. 3x is still enough to look at a
 *  detail up close; past it the source image would visibly pixelate on a
 *  typical phone screen anyway. */
export const MAX_SCALE = 3;
/** What a double tap zooms to, from resting (1x). */
export const DOUBLE_TAP_SCALE = 2;

/** Keeps a scale (from a pinch gesture, or anything else) inside
 *  [MIN_SCALE, MAX_SCALE]. */
export function clampScale(scale: number, min: number = MIN_SCALE, max: number = MAX_SCALE): number {
  if (!Number.isFinite(scale)) return min;
  return Math.min(max, Math.max(min, scale));
}

/** A pinch reports a ratio against the distance the two fingers started
 *  at, not an absolute scale — this multiplies that onto the scale the
 *  gesture began from and clamps the result. */
export function scaleFromPinch(startScale: number, distanceRatio: number): number {
  if (!Number.isFinite(distanceRatio) || distanceRatio <= 0) return clampScale(startScale);
  return clampScale(startScale * distanceRatio);
}

/** Double tap toggles: zoomed in already snaps back to 1x, otherwise jumps
 *  to `DOUBLE_TAP_SCALE`. A small epsilon above `MIN_SCALE` treats a scale
 *  left just a hair over 1x by a pinch that nearly returned to rest as
 *  still resting, so the next tap zooms in rather than doing nothing. */
export function nextDoubleTapScale(currentScale: number): number {
  return currentScale > MIN_SCALE + 0.01 ? MIN_SCALE : DOUBLE_TAP_SCALE;
}

/** Whether the image on screen changed (a new chapter's scene, or the
 *  story cover taking its place) — the full-screen view resets scale/pan
 *  whenever this is true, so a listener never ends up zoomed into a
 *  corner of the wrong picture after a chapter boundary or a seek. */
export function shouldResetZoomFor(previousKey: string | null, nextKey: string | null): boolean {
  return previousKey !== nextKey;
}

// ── Tap-to-close classification ─────────────────────────────────────────
//
// The full-screen view has no close button (product ask): a single tap on
// the image or the backdrop closes it instead, exactly like tapping the
// small illustration opened it. That single tap must not fire on a pan/
// drag or a pinch, and a genuine double tap must still zoom rather than
// close — so every finished pointer gesture needs classifying before
// deciding what, if anything, it means. The actual waiting (a real
// setTimeout, cancelled by a second tap) is wired in the component; this
// is only the yes/no decisions that drive it, kept pure and testable the
// same way as the rest of this module.

/** How far a pointer may move, in CSS pixels, and still count as a tap
 *  rather than a drag. Generous enough for a child's finger to wobble a
 *  little without losing the tap, tight enough that an actual pan (which
 *  moves the image, however slightly, under `zoom.scale > 1`) is never
 *  mistaken for one. */
export const TAP_MOVE_THRESHOLD_PX = 8;

/** How long after a first tap a second one still counts as a double tap
 *  (zoom) rather than two independent single taps (the second of which
 *  would otherwise close the view right after the first one very nearly
 *  did). */
export const DOUBLE_TAP_WINDOW_MS = 250;

export type PointerGestureKind = "tap" | "drag" | "pinch";

/** Classifies a finished single-contiguous-touch gesture (from the moment
 *  the first pointer went down to the moment the last one lifted) as a
 *  tap, a drag, or a pinch.
 *
 *  A pinch is decided the instant a second pointer joins, not by where
 *  the gesture ends: `maxPointers` is the highest number of simultaneous
 *  pointers seen at any point during it, so a two-finger touch that ends
 *  back down to one finger still reads as a pinch, never as a tap or
 *  drag. Only once `maxPointers` never exceeded one does how far that
 *  single pointer travelled decide tap vs. drag. */
export function classifyPointerGesture(args: {
  movedPx: number;
  maxPointers: number;
  moveThresholdPx?: number;
}): PointerGestureKind {
  if (args.maxPointers >= 2) return "pinch";
  return args.movedPx > (args.moveThresholdPx ?? TAP_MOVE_THRESHOLD_PX) ? "drag" : "tap";
}

/** Whether a tap landing at `nowMs` is the second tap of a double tap,
 *  given the timestamp of the previous one (`0` = none pending, e.g.
 *  already consumed by an earlier double tap, or this is the first tap
 *  since the view opened). */
export function isDoubleTap(previousTapAtMs: number, nowMs: number, windowMs: number = DOUBLE_TAP_WINDOW_MS): boolean {
  return previousTapAtMs > 0 && nowMs - previousTapAtMs < windowMs;
}
